'use client';

import { useCallback, useMemo } from 'react';

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

import SurveyAiFormsAiSectionQuestionTitle from './survey-ai-forms-ai-section-question-title';
import { useAiRankingQuestion } from './survey-ai-forms-ai-section-question.hooks';
import type { AISectionQuestionsBaseProps } from './survey-ai-forms-ai-section-questions.types';
import SurveyAiFormsInputComposer from './survey-ai-forms-input-composer';
import SurveyAiFormsRankingSortableItem from './survey-ai-forms-ranking-sortable-item';

interface Props extends AISectionQuestionsBaseProps {
  value: string[];
  onChange: (value: string[] | number | string) => void;
  loading?: boolean;
}

export default function SurveyAiFormsAiSectionRankingQuestion({
  question,
  value,
  transcriptValue = '',
  onChange,
  onTranscriptChange,
  onSubmit,
  onNext,
  hasValue = false,
  responseMode,
  isActive = true,
  onFocus,
  registerVoiceStopCallback,
  isUserVoiceEnabled = false,
  loading = false,
  isMobile = false,
}: Props) {
  // Use AI ranking question hook for business logic
  const {
    isListening,
    rankedOptions,
    availableOptions,
    toggleOption,
    handleDragEnd: handleDragEndFromHook,
    handleInputComposerSubmit,
    canSubmit,
  } = useAiRankingQuestion({
    question,
    value,
    transcriptValue,
    onChange,
    onSubmit,
    onNext,
    hasValue,
  });

  // Setup drag sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const sortableIds = useMemo(() => value || [], [value]);

  const handleAvailableOptionClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const optionValue = e.currentTarget.dataset.optionValue;
      if (optionValue) toggleOption(optionValue);
    },
    [toggleOption]
  );

  return (
    <div
      data-test='ranking-question'
      className='flex w-full flex-col gap-6 md:gap-8'
    >
      {/* Question Title */}
      <SurveyAiFormsAiSectionQuestionTitle
        text={question.text}
        assets={question.assets}
      />

      {/* Ranking Options */}
      <div className='flex w-full flex-col gap-2'>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEndFromHook}
        >
          <SortableContext
            items={sortableIds}
            strategy={verticalListSortingStrategy}
          >
            {rankedOptions.map((option, index) => {
              if (!option) return null;
              return (
                <SurveyAiFormsRankingSortableItem
                  key={option.value}
                  id={option.value}
                  label={option.label}
                  variant='ranked'
                  index={index}
                  onClick={toggleOption}
                />
              );
            })}
          </SortableContext>
        </DndContext>

        {/* Available (unranked) options */}
        {availableOptions.map((option) => (
          <button
            key={option.id}
            type='button'
            onClick={handleAvailableOptionClick}
            data-option-value={option.value}
            data-test='ranking-option-available'
            className='flex h-10 w-full cursor-pointer items-center justify-between rounded-[48px] bg-(--brand-bg-unselected) px-6 backdrop-blur-xs backdrop-filter'
          >
            <p className='[font-family:var(--brand-font)] text-sm/5 font-normal text-(--brand-text-unselected)'>
              {option.label}
            </p>
          </button>
        ))}
      </div>

      {/* Input Composer - for AI transcript input */}
      <SurveyAiFormsInputComposer
        value={transcriptValue}
        onChange={onTranscriptChange}
        onSubmit={handleInputComposerSubmit}
        responseMode={responseMode}
        isActive={isActive}
        onFocus={onFocus}
        registerVoiceStopCallback={registerVoiceStopCallback}
        instanceId={question.id}
        canSubmit={canSubmit}
        isListening={isListening}
        questionType={question.type}
        loading={loading}
        isMobile={isMobile}
        isUserVoiceEnabled={isUserVoiceEnabled}
      />
    </div>
  );
}
